/**
 * AdminPrivacyRequests — platform_admin console for CCPA/CPRA + GDPR
 * data subject request tickets. List, filter by status, and update
 * status/notes inline.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, RefreshCw, User, Mail, Calendar, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "open", label: "Open" },
    { key: "in_review", label: "In review" },
    { key: "identity_verification_needed", label: "ID needed" },
    { key: "resolved", label: "Resolved" },
    { key: "rejected", label: "Rejected" },
];

const STATUS_OPTIONS = ["open", "in_review", "identity_verification_needed", "resolved", "rejected"];

const TYPE_LABEL = {
    access: "Access",
    delete: "Delete",
    correct: "Correct",
    port: "Portability",
    opt_out_sale: "Opt out",
    restrict: "Restrict",
    other: "Other",
};

const STATUS_TONE = {
    open: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    in_review: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    identity_verification_needed: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
};

const RowCard = ({ row, onUpdate }) => {
    const [status, setStatus] = useState(row.status);
    const [notes, setNotes] = useState(row.admin_notes || "");
    const [saving, setSaving] = useState(false);
    const dirty = status !== row.status || (notes || "") !== (row.admin_notes || "");

    const save = async () => {
        setSaving(true);
        try {
            const patch = {};
            if (status !== row.status) patch.status = status;
            if ((notes || "") !== (row.admin_notes || "")) patch.admin_notes = notes;
            await api.patch(`/admin/privacy-requests/${row.id}`, patch);
            toast.success("Updated.");
            onUpdate();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card-soft p-5 space-y-3" data-testid={`dsr-row-${row.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <code className="font-mono text-[11px] text-muted-foreground">{row.id}</code>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[row.status] || "bg-muted"}`} data-testid={`dsr-status-${row.id}`}>
                            {row.status}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-semibold text-foreground">
                            {TYPE_LABEL[row.type] || row.type}
                        </span>
                        <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{row.jurisdiction}</span>
                    </div>
                    <div className="mt-2 text-sm text-foreground flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><User size={12} /> {row.name}</span>
                        <span className="inline-flex items-center gap-1"><Mail size={12} /> <a href={`mailto:${row.email}`} className="underline hover:text-foreground">{row.email}</a></span>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Calendar size={12} /> {new Date(row.created_at).toLocaleString()}</span>
                    </div>
                    {row.is_authorized_agent && (
                        <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                            Authorized agent: <b>{row.agent_name || "(unnamed)"}</b>
                        </div>
                    )}
                </div>
            </div>

            {row.details && (
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-foreground whitespace-pre-wrap">{row.details}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 items-start">
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid={`dsr-status-select-${row.id}`}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Internal notes (visible to admins only)…"
                    data-testid={`dsr-notes-${row.id}`}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid={`dsr-save-${row.id}`}
                    className="btn-green inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : "Update"}
                </button>
            </div>
        </div>
    );
};

export default function AdminPrivacyRequests() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [status, setStatus] = useState("open");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = status === "all" ? {} : { status };
            const { data } = await api.get("/admin/privacy-requests", { params });
            setRows(data.rows || []);
            setCounts(data.counts || {});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load requests.");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const totalOpen = useMemo(() => (counts.open || 0) + (counts.in_review || 0) + (counts.identity_verification_needed || 0), [counts]);

    return (
        <div className="container-app py-10" data-testid="admin-privacy-requests">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Privacy</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">Data subject requests</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        CCPA/CPRA + GDPR privacy tickets. {totalOpen} awaiting action.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="btn-outline inline-flex items-center gap-2"
                    data-testid="dsr-refresh"
                >
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="dsr-status-tabs">
                {STATUS_TABS.map((t) => {
                    const count = t.key === "all"
                        ? (counts.open || 0) + (counts.in_review || 0) + (counts.identity_verification_needed || 0) + (counts.resolved || 0) + (counts.rejected || 0)
                        : (counts[t.key] || 0);
                    const active = status === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setStatus(t.key)}
                            data-testid={`dsr-tab-${t.key}`}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-brand-navy text-white" : "bg-muted/40 text-foreground hover:bg-muted"}`}
                        >
                            {t.label}
                            <span className="rounded-full bg-black/10 dark:bg-white/10 px-1.5 py-0.5 text-[10px]">{count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-6 space-y-4">
                {loading && (
                    <div className="py-10 flex justify-center" data-testid="dsr-loading">
                        <Loader2 className="animate-spin text-emerald-500" />
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="card-soft p-10 text-center" data-testid="dsr-empty">
                        <MessageSquare size={24} className="mx-auto text-muted-foreground" />
                        <div className="mt-2 font-heading font-semibold text-lg text-foreground">No requests here.</div>
                        <p className="mt-1 text-sm text-muted-foreground">The queue&apos;s empty. Nice.</p>
                    </div>
                )}
                {!loading && rows.map((r) => <RowCard key={r.id} row={r} onUpdate={load} />)}
            </div>
        </div>
    );
}
